<template>
  <div class="forgot-password-page">
    <div class="container-fluid p-0">
      <div class="row g-0 min-vh-100">
        <!-- Left Side: Cover Image and Quote -->
        <div class="col-xl-8 col-lg-7 d-none d-lg-block position-relative">
          <div class="cover-image-panel" :style="backgroundStyle">
            <div class="bg-overlay-gradient"></div>
            <div class="left-panel-content d-flex h-100 flex-column justify-content-between p-5 text-white">
              <div class="brand-header">
                <img :src="county_logo()" alt="Logo" class="brand-logo" />
              </div>
              <div class="quote-container my-auto text-center mx-auto max-width-lg">
                <i class="bx bxs-quote-alt-left quote-icon mb-3"></i>
                <h3 class="quote-text mb-3">Right there for you</h3>
                <p class="system-subtitle">Homabay County HR System</p>
              </div>
              <div class="footer-copyright">
                <p class="mb-0">&copy; {{ currentYear }} <strong>Homabay County HRMIS</strong>. All rights reserved.</p>
              </div>
            </div>
          </div>
        </div>
        <!-- End Left Side -->

        <!-- Right Side: Auth Form Card -->
        <div class="col-xl-4 col-lg-5 col-12 d-flex align-items-center justify-content-center bg-light-panel">
          <div class="auth-card-wrapper p-4 p-md-5">
            <div class="auth-card-body text-center">
              <!-- Mobile Logo (shown only on small screens) -->
              <div class="d-block d-lg-none mb-4 pb-2">
                <img :src="county_logo()" alt="Logo" class="mobile-brand-logo" />
              </div>

              <!-- Step 1: Request Reset -->
              <div v-if="currentStep === 'request'">
                <div class="modern-badge modern-badge--primary mx-auto mb-4">
                  <i class="bx bx-key"></i>
                  <div class="badge-ring"></div>
                </div>

                <div class="auth-header mb-4">
                  <h4 class="auth-title">Forgot Password?</h4>
                  <p class="auth-subtitle">Enter your email or ID number and we'll send a temporary password to your registered email and phone (SMS)</p>
                </div>

                <div class="mt-4">
                  <div class="form-group-modern mb-4 text-start">
                    <label for="identifier" class="label-modern">Email Address or ID Number</label>
                    <div class="input-wrapper-modern">
                      <span class="input-icon-left"><i class="bx bx-user"></i></span>
                      <input
                          v-model="identifier"
                          type="text"
                          class="input-modern"
                          id="identifier"
                          placeholder="Enter your email or ID number"
                          @keyup.enter="requestReset"
                          :disabled="isLoading"
                          required
                      >
                    </div>
                  </div>

                  <!-- Alert Messages -->
                  <transition name="fade">
                    <div v-if="alert.success" class="modern-alert modern-alert--success mb-4" role="alert">
                      <i class="bx bx-check-circle alert-icon"></i>
                      <span class="alert-text">{{ message }}</span>
                    </div>
                  </transition>

                  <transition name="fade">
                    <div v-if="alert.info" class="modern-alert modern-alert--info mb-4" role="alert">
                      <i class="bx bx-info-circle alert-icon"></i>
                      <span class="alert-text">{{ message }}</span>
                    </div>
                  </transition>

                  <transition name="fade">
                    <div v-if="alert.failed" class="modern-alert modern-alert--danger mb-4" role="alert">
                      <i class="bx bx-error-circle alert-icon"></i>
                      <span class="alert-text">{{ message }}</span>
                    </div>
                  </transition>

                  <div class="mt-3">
                    <button
                        @click="requestReset"
                        class="btn-modern btn-modern--primary w-100"
                        type="button"
                        :disabled="isLoading || !identifier"
                    >
                      <span v-if="!isLoading" class="d-flex align-items-center justify-content-center gap-2">
                        <i class="bx bx-paper-plane"></i> Send temporary password
                      </span>
                      <span v-else class="d-flex align-items-center justify-content-center gap-2">
                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        Sending…
                      </span>
                    </button>
                  </div>

                  <div class="mt-4 text-center">
                    <a href="javascript:void(0);" @click="goToLogin" class="back-link">
                      <i class="bx bx-arrow-back me-1"></i> Back to Login
                    </a>
                  </div>
                </div>
              </div>

              <!-- Step 2: Success Message -->
              <div v-if="currentStep === 'success'">
                <div class="modern-badge modern-badge--success mx-auto mb-4">
                  <i class="bx bx-check-shield"></i>
                  <div class="badge-ring"></div>
                </div>

                <div class="auth-header mb-4">
                  <h4 class="auth-title text-success">Temporary Password Sent</h4>
                  <p class="auth-subtitle">{{ successDetail }}</p>
                </div>

                <!-- Next Steps Card -->
                <div class="modern-card card-glow text-start mb-4">
                  <div class="card-header-modern">
                    <i class="bx bx-directions header-icon text-info"></i>
                    <h5 class="card-title-modern">Next Steps</h5>
                  </div>
                  <div class="card-body-modern">
                    <div class="requirements-checklist">
                      <div class="checklist-item checklist-item--info" v-if="resetChannels.email">
                        <div class="checkbox-indicator text-info">
                          <i class="bx bx-envelope"></i>
                        </div>
                        <span class="checklist-text">Check your email inbox (and spam folder)</span>
                      </div>
                      <div class="checklist-item checklist-item--info" v-if="resetChannels.sms">
                        <div class="checkbox-indicator text-info">
                          <i class="bx bx-message-rounded-dots"></i>
                        </div>
                        <span class="checklist-text">Check SMS on your registered phone</span>
                      </div>
                      <div class="checklist-item">
                        <div class="checkbox-indicator text-secondary">
                          <i class="bx bx-log-in-circle"></i>
                        </div>
                        <span class="checklist-text">Sign in with the temporary password</span>
                      </div>
                      <div class="checklist-item">
                        <div class="checkbox-indicator text-secondary">
                          <i class="bx bx-refresh"></i>
                        </div>
                        <span class="checklist-text">Change your password immediately after logging in</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mt-4 d-grid gap-3">
                  <button
                      @click="goToLogin"
                      class="btn-modern btn-modern--primary w-100"
                      type="button"
                  >
                    <i class="bx bx-log-in"></i> Go to Login
                  </button>
                  
                  <div class="text-center mt-2">
                    <a href="javascript:void(0);" @click="resendEmail" class="resend-link">
                      <i class="bx bx-refresh me-1"></i> Didn't receive it? Send again
                    </a>
                  </div>
                </div>
              </div>

              <!-- Footer info shown only on mobile -->
              <div class="d-block d-lg-none mt-5 text-center text-muted small">
                <p class="mb-0">&copy; {{ currentYear }} <strong>Homabay County HRMIS</strong></p>
              </div>

            </div>
          </div>
        </div>
        <!-- End Right Side -->
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, county_logo, login_bg, execute2 } from "@/api";
import router from "@/router";

export default {
  name: "ForgotPassword",
  data() {
    return {
      backgroundUrl: login_bg,
      identifier: '',
      isLoading: false,
      message: '',
      alert: {
        success: false,
        info: false,
        failed: false
      },
      currentStep: 'request', // 'request' or 'success'
      currentYear: new Date().getFullYear(),
      successDetail: '',
      resetChannels: { email: false, sms: false },
    }
  },
  computed: {
    backgroundStyle() {
      return {
        backgroundImage: `url(${this.backgroundUrl})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
      };
    },
  },
  methods: {
    county_logo() {
      return county_logo;
    },

    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
    },

    goToLogin() {
      router.push('/');
    },

    async requestReset() {
      // Validate input
      if (!this.identifier || this.identifier.trim() === '') {
        this.clearAlerts();
        this.message = "Please enter your email or ID number";
        this.alert.failed = true;
        return;
      }

      this.clearAlerts();
      this.message = "Sending temporary password…";
      this.alert.info = true;
      this.isLoading = true;

      const data = new FormData();
      data.append("function", "forgotPassword");
      data.append("identifier", this.identifier.trim());

      try {
        const res = await execute2(data, authUrl);

        this.clearAlerts();
        this.isLoading = false;

        if (res.data.success) {
          const payload = res.data.data || {};
          this.resetChannels = {
            email: !!payload.emailSent,
            sms: !!payload.smsSent,
          };
          this.successDetail = res.data.message || "Check your email and phone for the temporary password.";
          this.currentStep = 'success';
          this.message = this.successDetail;
        } else {
          this.alert.failed = true;
          this.message = res.data.message || "Could not send the temporary password. Please try again or contact HR.";
        }
      } catch (error) {
        this.clearAlerts();
        this.isLoading = false;
        this.alert.failed = true;
        const apiMsg = error?.response?.data?.message;
        this.message = apiMsg || error.message || "An error occurred. Please try again.";
        console.error("Forgot password error:", error);
      }
    },

    async resendEmail() {
      // Simply call requestReset again
      this.currentStep = 'request';
      this.clearAlerts();
      await this.requestReset();
    }
  }
}
</script>

<style scoped>
.forgot-password-page {
  --primary-color: #2195d3;
  --primary-hover: #176f9d;
  --primary-glow: rgba(33, 149, 211, 0.15);
  --success-color: #10b981;
  --success-glow: rgba(16, 185, 129, 0.15);
  --warning-color: #f59e0b;
  --danger-color: #ef4444;
  --danger-glow: rgba(239, 68, 68, 0.15);
  --card-bg: #ffffff;
  --input-bg: #f8fafc;
  --border-color: #e2e8f0;
  --text-primary: #1e293b;
  --text-secondary: #64748b;
  
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: var(--text-primary);
  background-color: #f8fafc;
  min-height: 100vh;
}

/* Left Cover Image Panel */
.cover-image-panel {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  height: 100%;
  width: 100%;
}
.bg-overlay-gradient {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(135deg, rgba(22, 54, 92, 0.85) 0%, rgba(33, 149, 211, 0.85) 100%);
  z-index: 1;
}
.left-panel-content {
  position: relative;
  z-index: 2;
  height: 100%;
}
.brand-logo {
  max-width: 180px;
  height: auto;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}
.quote-icon {
  font-size: 48px;
  color: var(--success-color);
  opacity: 0.8;
  display: block;
}
.quote-text {
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}
.system-subtitle {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}
.max-width-lg {
  max-width: 480px;
}

/* Right Panel Styles */
.bg-light-panel {
  background-color: #f8fafc;
  min-height: 100vh;
}
.auth-card-wrapper {
  width: 100%;
  max-width: 460px;
}
.auth-card-body {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03), 0 1px 3px rgba(0, 0, 0, 0.01);
  padding: 40px 30px;
}
.mobile-brand-logo {
  max-width: 150px;
  height: auto;
}

/* Pulse Badge Header */
.modern-badge {
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: white;
  z-index: 2;
}
.modern-badge--primary {
  background: linear-gradient(135deg, var(--primary-color) 0%, #16365c 100%);
  box-shadow: 0 6px 15px rgba(33, 149, 211, 0.3);
}
.modern-badge--success {
  background: linear-gradient(135deg, var(--success-color) 0%, #047857 100%);
  box-shadow: 0 6px 15px rgba(16, 185, 129, 0.3);
}
.badge-ring {
  position: absolute;
  top: -6px; left: -6px; right: -6px; bottom: -6px;
  border: 2px solid currentColor;
  border-radius: 50%;
  opacity: 0.15;
  animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
@keyframes pulse-ring {
  0%, 100% { transform: scale(1); opacity: 0.15; }
  50% { transform: scale(1.1); opacity: 0.3; }
}

.auth-title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.02em;
}
.auth-subtitle {
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.5;
  margin-top: 6px;
}

/* Modern Input Styling */
.label-modern {
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #475569;
  margin-bottom: 6px;
}
.input-wrapper-modern {
  position: relative;
  display: flex;
  align-items: center;
}
.input-icon-left {
  position: absolute;
  left: 14px;
  color: #94a3b8;
  font-size: 18px;
  transition: color 0.2s;
  display: flex;
  align-items: center;
}
.input-modern {
  width: 100%;
  padding: 12px 16px 12px 42px;
  background-color: var(--input-bg);
  border: 1.5px solid var(--border-color);
  border-radius: 10px;
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s ease;
  outline: none;
}
.input-modern:focus {
  border-color: var(--primary-color);
  background-color: white;
  box-shadow: 0 0 0 4px var(--primary-glow);
}
.input-modern:focus ~ .input-icon-left {
  color: var(--primary-color);
}
.input-modern:disabled {
  background-color: #e2e8f0;
  color: #94a3b8;
  cursor: not-allowed;
}

/* Modernized Notification Alerts */
.modern-alert {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  text-align: left;
}
.alert-icon {
  font-size: 18px;
  margin-top: 2px;
  flex-shrink: 0;
}
.alert-text {
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
}
.modern-alert--success {
  background-color: #f0fdf4;
  border-color: rgba(16, 185, 129, 0.2);
  color: #166534;
}
.modern-alert--info {
  background-color: rgba(33, 149, 211, 0.06);
  border-color: rgba(33, 149, 211, 0.2);
  color: #1e4976;
}
.modern-alert--danger {
  background-color: #fef2f2;
  border-color: rgba(239, 68, 68, 0.2);
  color: #991b1b;
}

/* Buttons modern styling */
.btn-modern {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  outline: none;
}
.btn-modern--primary {
  background: linear-gradient(135deg, var(--primary-color) 0%, #16365c 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(33, 149, 211, 0.25);
}
.btn-modern--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(33, 149, 211, 0.35);
  filter: brightness(1.05);
}
.btn-modern--primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Links */
.back-link, .resend-link {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  text-decoration: none;
  transition: color 0.2s;
  display: inline-flex;
  align-items: center;
}
.back-link:hover, .resend-link:hover {
  color: var(--primary-color);
}

/* Success Card details */
.modern-card {
  background: #f8fafc;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  overflow: hidden;
}
.card-glow {
  position: relative;
}
.card-glow::after {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0; height: 3px;
  background: linear-gradient(90deg, var(--primary-color), #06b6d4);
  opacity: 0.8;
}
.card-header-modern {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid var(--border-color);
}
.card-title-modern {
  font-size: 13px;
  font-weight: 700;
  margin: 0;
  color: #111827;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.header-icon {
  font-size: 16px;
}
.card-body-modern {
  padding: 16px;
}
.requirements-checklist {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.checklist-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.checkbox-indicator {
  font-size: 15px;
  flex-shrink: 0;
  margin-top: 1px;
}
.checklist-text {
  font-size: 12.5px;
  color: var(--text-secondary);
  line-height: 1.4;
}
.checklist-item--info .checklist-text {
  color: #1e293b;
  font-weight: 500;
}

/* Spinner */
.spinner-border {
  animation: spinner-border .75s linear infinite;
}
@keyframes spinner-border {
  to { transform: rotate(360deg); }
}

/* Fade animation */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Mobile responsive adjustments */
@media (max-width: 575.98px) {
  .auth-card-body {
    padding: 30px 20px;
    border-radius: 12px;
  }
}
</style>